import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Avatar,
  List,
  ListItem,
  TextareaAutosize,
  CircularProgress,
} from '@mui/material';
import {
  CalendarIcon,
  EllipsisIcon,
  GamifyIcon,
} from '../../../components/GamifyIcon';
import { API, graphqlOperation } from 'aws-amplify';
import * as mutations from '../../../../graphql/mutations';
import DealOptionsMenu from '../DealOptionsMenu';
// import DealNoteOptionsMenu from '../DealNoteOptionsMenu';
import Moment from 'moment/moment';

import { Formik } from 'formik';
import { GamifyToast } from '../../../common/CustomToasts';
import {
  getTZ,
  getCurrentUTCDate,
  newLocalToISODateFormat,
  getLocalDateTime,
} from '../../../common/Utilities';
import DynamicConfirmModal from '../../../common/DynamicConfirmModal';
import ImageCarousel from '../../../common/ImageCarousel';
import { usePermissions } from '../../../context/PermissionsContext/PermissionsContext';
import { PermissionKeys } from '../../../context/PermissionsContext/permissionTypes';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';
import DealTabbedContent from '../../../components/DealTabbedContent/DealTabbedContent';

export default function SelectedDealDisplay({ props }) {
  const {
    deal,
    isOnKanban,
    payEngineStatus,
    mainDisplayAttrs,
    dealStage,
    appointments,
    comments,
    metaData,
    setAddDealOpen,
    setSortOpen,
    selectedDealMetaDataOpen,
    setSelectedDealMetaDataOpen,
    handleOpenOwnerDealStageModal,
    handleOpenChargeModal,
    handleDeleteDeal,
    handleRefetchDeals,
    onTriggerSelectedDealRefetch,
    userId,
    dealId,
    categoryId,
    stageId,
    hideDealMetaData,
    hideDealOptionsMenu,
    isReadOnly,
  } = props;

  const { hasPermission } = usePermissions();
  const { theme } = useTheme();
  // create an array of owner ids and filter out any falsey values
  const ownersIds = [deal?.setter?.user?.id, deal?.closer?.user?.id].filter(
    Boolean
  );

  // When isReadOnly is true (e.g., standalone deal page for estimators), force showEdit to false
  const showEdit =
    !isReadOnly && hasPermission(PermissionKeys.Sales_UpdateDeal, ownersIds);
  const showDelete =
    !isReadOnly && hasPermission(PermissionKeys.Sales_DeleteDeal, ownersIds);
  const showMenu = showEdit || showDelete; /* || showSchedule; */

  const [anchorEl, setAnchorEl] = useState(null);
  const [addNoteFocused, setAddNoteFocused] = useState(false);
  // const [notesAnchorElMap, setNotesAnchorElMap] = useState({});
  const [editNoteId, setEditNoteId] = useState(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imageIndex, setImageIndex] = useState(null);
  const [showImageCarousel, setShowImageCarousel] = useState(false);
  const [showConfirmDeleteImage, setShowConfirmDeleteImage] = useState(false);
  const [carouselImages, setCarouselImages] = useState([]);
  const [carouselPhotos, setCarouselPhotos] = useState([]);

  let contactType = props.contactType;
  if (contactType === undefined) {
    contactType = '';
  }
  let dispositionID = props.dispositionID;
  if (dispositionID === undefined) {
    dispositionID = '';
  }

  const newDealContact = {
    contactType,
    dispositionID,
    newStageID: stageId,
    notes: '',
    notesMap: {},
    value: 0,
    imageName: '',
    imageType: '',
    dealID: dealId,
    categoryID: categoryId,
  };
  const [initialValues, setInitialValues] = useState(newDealContact);

  useEffect(() => {
    const newNotesMap = {};

    for (const comment of comments) {
      newNotesMap[comment.id] = comment.comment;
    }

    setInitialValues({
      contactType,
      dispositionID,
      newStageID: stageId,
      notes: '',
      notesMap: newNotesMap,
      value: 0,
      imageName: '',
      imageType: '',
      dealID: dealId,
      categoryID: categoryId,
    });
  }, [dealId]);

  useEffect(() => {
    // Need to reset the edit note id, notes focused map, and notes anchor el map whenver comments change so each map is always up to date
    const newNotesAnchorElMap = {};
    const newNotesMap = {};

    for (const comment of comments) {
      newNotesAnchorElMap[comment.id] = null;
      newNotesMap[comment.id] = comment.comment;
    }

    setEditNoteId(null);
    // setNotesAnchorElMap(newNotesAnchorElMap);
    setInitialValues({
      contactType,
      dispositionID,
      newStageID: stageId,
      notes: '',
      notesMap: newNotesMap,
      value: 0,
      imageName: '',
      imageType: '',
      dealID: dealId,
      categoryID: categoryId,
    });
  }, [comments]);

  const handleMenuOpen = (e) => {
    setAnchorEl(e.currentTarget);
    if (setAddDealOpen) {
      setAddDealOpen(false);
    }
    if (setSortOpen) {
      setSortOpen(false);
    }
  };

  // const handleNoteOptionsOpen = (e, dealNoteId, formikProps) => {
  //   setNotesAnchorElMap((prev) => ({
  //     ...prev,
  //     [dealNoteId]: e.target,
  //   }));
  //   if (setAddDealOpen) {
  //     setAddDealOpen(false);
  //   }
  //   if (setSortOpen) {
  //     setSortOpen(false);
  //   }
  //   setEditNoteId(null);
  //   formikProps.resetForm();
  // };

  const handleImageClick = (index) => {
    setImageIndex(index);
    setTimeout(() => {
      setShowImageCarousel(true);
    }, 250);
  };

  const handleMetaDataClick = () => {
    setSelectedDealMetaDataOpen(!selectedDealMetaDataOpen);
  };

  function formatScheduleDate(date, minutesPastMidnight) {
    const zeroPad = (num, places) => String(num).padStart(places, '0');

    const hours = Math.trunc(minutesPastMidnight / 60);
    const minutes = minutesPastMidnight - hours * 60;
    const timeStr = `${zeroPad(hours, 2)}:${zeroPad(minutes, 2)}`;
    const momentDate = new Moment(`${date}T${timeStr}`);
    return momentDate.format('dddd, MMMM Do, h:mm a');
  }

  let powerMainDisplayAttrs;
  const kwsMainDisplayAttrs = [];

  // put power and kws attrs into separate objects, they won't render w/ the rest of mainDisplayAttrs later b/c their vals are objects not strings
  for (let i = 0; i < mainDisplayAttrs.length; i++) {
    const attr = mainDisplayAttrs[i];

    if (typeof attr.val === 'object') {
      if (attr.val?.type === 'Power') {
        powerMainDisplayAttrs = attr;
      }
      if (attr.val?.type === 'kws') {
        if (attr.val.val !== '' && attr.val.val !== '$ ') {
          kwsMainDisplayAttrs.push({ label: [attr.label], val: attr.val.val });
        }
      }
    }
  }

  // Image processing using real photo objects from deal.images
  // Now properly fetches photo metadata from backend instead of hardcoded data

  // Data cleanup function to ensure both photo fields are synchronized and clean
  const cleanupPhotoData = async () => {
    try {
      const flexAttributes = JSON.parse(deal.flexAttributes);
      let hasChanges = false;

      // Helper function to validate and clean image arrays
      const cleanImageArray = (images) => {
        if (!Array.isArray(images)) return [];
        return images.filter(
          (img) =>
            img &&
            typeof img === 'string' &&
            img !== 'undefined' &&
            img !== 'null' &&
            img.trim() !== '' &&
            !img.includes('undefined')
        );
      };

      // Clean both arrays
      const originalPhotoLength = flexAttributes.photo?.length || 0;
      const originalPicturesLength = flexAttributes.pictures?.length || 0;

      flexAttributes.photo = cleanImageArray(flexAttributes.photo);
      flexAttributes.pictures = cleanImageArray(flexAttributes.pictures);

      // Sync arrays - combine unique images from both
      const allUniqueImages = Array.from(
        new Set([...flexAttributes.photo, ...flexAttributes.pictures])
      );

      // Check if cleanup is needed
      if (
        originalPhotoLength !== flexAttributes.photo.length ||
        originalPicturesLength !== flexAttributes.pictures.length ||
        flexAttributes.photo.length !== allUniqueImages.length ||
        flexAttributes.pictures.length !== allUniqueImages.length
      ) {
        hasChanges = true;
        flexAttributes.photo = allUniqueImages;
        flexAttributes.pictures = allUniqueImages;
      }

      // Update database if changes were made
      if (hasChanges) {
        console.log('Cleaning up photo data inconsistencies...');
        deal.flexAttributes = JSON.stringify(flexAttributes);

        const ourDeal = {
          id: deal.id,
          name: deal.name,
          status: deal.status,
          imageName: deal.imageName,
          imageType: deal.imageType,
          currentStageID: deal.currentStageID,
          currentOwnerID: deal.currentOwnerID,
          currentOwnerType: deal.currentOwnerType,
          orgID: deal.orgID,
          isDeleted: false,
          flexAttributes: deal.flexAttributes,
          categoryID: deal.categoryID,
          tmst: newLocalToISODateFormat(getLocalDateTime()),
        };

        await API.graphql(
          graphqlOperation(mutations.maintainDealV3, {
            dealInfo: JSON.stringify(ourDeal),
          })
        );
      }
    } catch (error) {
      console.error('Error during photo data cleanup:', error);
    }
  };

  // Run cleanup on component mount if needed
  useEffect(() => {
    if (deal?.flexAttributes) {
      cleanupPhotoData();
    }
  }, [deal?.id]); // Only run when deal changes

  // Use real photo objects from deal.images

  async function createFeedItem(values) {
    let myDoneActionCount;

    if (values.value === '' || values.value === '0') {
      myDoneActionCount = Number(1);
    } else {
      myDoneActionCount = Number(values.value);
    }

    try {
      let dealID = values.dealID;
      if (dealID === '') {
        dealID = 'NO_DEAL';
      }
      const now = new Date();
      const completeMissionInput = {
        currentOwnerID: values.currentOwnerID,
        userMissionID: '',
        completedFlag: false,
        myDoneActionCount,
        doneImage: values.imageName,
        doneImageType: values.imageType,
        status: 'completed',
        extID: '',
        comments: values.notes,
        dealID,
        dispositionID: values.dispositionID,
        followupTZ: getTZ(),
        contactType: values.contactType,
        categoryID: values.categoryID,
        tmst: now.toISOString(),
      };
      const completeMissionRes = await API.graphql({
        query: mutations.completeMission,
        variables: {
          missionInfo: JSON.stringify(completeMissionInput),
        },
      });
      try {
        const addressRcd = {
          id: dealID,
          lastVisited: getCurrentUTCDate(),
        };
        API.graphql(
          graphqlOperation(mutations.updateAddress, { input: addressRcd })
        );
      } catch (err) {
        console.warn('completeMission() - updateAddress error: ', err);
      }
      const completeMissionStatus = JSON.parse(
        completeMissionRes.data.completeMission
      );
      console.log('status: ', completeMissionStatus);
      if (completeMissionStatus.status === 'failed') {
        GamifyToast.error(
          global.appSettings.labels.mission.proper + ' save failed'
        );
        return false;
      } else if (completeMissionStatus.status === 'available') {
        GamifyToast.error(
          global.appSettings.labels.mission.proper + ' Abandoned'
        );
        return true;
      } else if (completeMissionStatus.status === 'success') {
        GamifyToast.success('Note added successfully');
        return true;
      } else if (completeMissionStatus.status.startsWith('WARNING:')) {
        console.error(completeMissionStatus.status);
        GamifyToast.error(completeMissionStatus.status);
        return false;
      } else {
        console.error(
          'error in completeMission(): ' + completeMissionStatus.status
        );
        GamifyToast.error(
          completeMissionStatus.status + '.   Gamify support has been notified.'
        );
        return false;
      }
    } catch (err) {
      console.error('!!error in completeMission(): ', JSON.stringify(err));
      GamifyToast.error(
        "We couldn't add your notes",
        'Please check your internet connection and try again'
      );
    }
  }

  async function doSubmit(values, actions) {
    if (values.notes !== '') {
      // Add a note
      const ret = await createFeedItem(values);
      if (ret) {
        // NOTE: Toasts are handled in createFeedItem
        actions.resetForm(values);

        // Refetch deals doesn't need args for deals list, kanban needs the stage id and to put false for showLoading
        if (!isOnKanban) {
          handleRefetchDeals();
        } else {
          // NOTE: null sortOrder results in using the current selected sortOrder of that dealStage
          handleRefetchDeals([deal.currentStageID], null, false);
        }

        onTriggerSelectedDealRefetch();
      }
      actions.setSubmitting(false);
    } else {
      try {
        //
        // Edit a note
        //
        await API.graphql(
          graphqlOperation(mutations.updateDealComments, {
            input: {
              id: editNoteId,
              comment: values.notesMap[editNoteId],
              updatedAt: new Date(),
            },
          })
        );

        GamifyToast.success('Note updated successfully');
        setEditNoteId(null);
        onTriggerSelectedDealRefetch();
      } catch (err) {
        console.error(err);
        GamifyToast.error(
          'There was an error updating the note, please try again'
        );
      }
    }
  }

  // Photo editing modal handlers (using real photo objects)

  return (
    <>
      <ImageCarousel
        showImageCarousel={showImageCarousel}
        setShowImageCarousel={setShowImageCarousel}
        imagesArr={carouselImages}
        imageIndex={imageIndex}
        setImageIndex={setImageIndex}
        zIndex={100002}
        photos={carouselPhotos}
      ></ImageCarousel>

      <DynamicConfirmModal
        showDynamicConfirmModal={showConfirmDeleteImage}
        setShowDynamicConfirmModal={setShowConfirmDeleteImage}
        zIndex={100002}
        title={`Are you sure you want to remove this image?`}
        subtitle={`This action cannot be undone.`}
        leftAlignText={true}
        confirmButtonColor={'#E35050'}
        isSaving={false}
        onCancel={() => {
          setShowConfirmDeleteImage(false);
        }}
        onConfirm={() => {
          // This modal is not used anymore - DealPhotos handles its own delete confirmation
          setShowConfirmDeleteImage(false);
        }}
      ></DynamicConfirmModal>

      <Box>
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          onSubmit={(values, actions) => doSubmit(values, actions)}
        >
          {(formikProps) => (
            <>
              <Box
                className="selected-deal-section"
                style={{ backgroundColor: theme.background3 }}
              >
                <Box className={'selected-deal-name-stage-section'}>
                  <Typography
                    className={'selected-deal-full-name-text'}
                    style={{ color: theme.text }}
                  >
                    {deal.name}
                  </Typography>
                  <Box className={'selected-deal-name-stage-section-right'}>
                    <Box
                      style={{
                        width: 'fit-content',
                        minWidth: 'fit-content',
                        backgroundColor: theme.primaryLight,
                        borderRadius: 1000,
                      }}
                    >
                      <Typography
                        className={'selected-deal-stage-text'}
                        style={{ color: theme.text }}
                      >
                        {dealStage || deal?.currentStage}
                      </Typography>
                    </Box>
                    {!hideDealOptionsMenu && showMenu && (
                      <>
                        <Button
                          disableRipple={true}
                          className={'ellipsis-button'}
                          onClick={(e) => handleMenuOpen(e)}
                        >
                          <EllipsisIcon></EllipsisIcon>
                        </Button>

                        <DealOptionsMenu
                          props={{
                            dealId,
                            anchorEl,
                            setAnchorEl,
                            payEngineStatus,
                            handleOpenOwnerDealStageModal,
                            handleOpenChargeModal,
                            stageId: deal.currentStageID,
                            handleDeleteDeal,
                            userId,
                            setter: deal.setter,
                            closer: deal.closer,
                          }}
                        ></DealOptionsMenu>
                      </>
                    )}
                  </Box>
                </Box>
                <Typography
                  className={'selected-deal-section-header-text'}
                  style={{ color: theme.textLight }}
                >
                  ABOUT
                </Typography>
                <List>
                  {mainDisplayAttrs.length > 0 &&
                    mainDisplayAttrs.map((attr, i) => (
                      <ListItem
                        key={i}
                        sx={{
                          padding: 0,
                        }}
                      >
                        {(typeof attr.val === 'string' ||
                          typeof attr.val === 'number') &&
                          attr.val !== '' && (
                            <Box
                              className={'selected-deal-attribute-container'}
                            >
                              <Typography
                                className={'selected-deal-attribute-label-text'}
                                style={{ color: theme.textLight }}
                              >
                                {attr.label}
                                {attr.label !== '' ? ':' : ''}
                              </Typography>
                              <Typography
                                className={'selected-deal-attribute-val-text'}
                                style={{ color: theme.text }}
                              >
                                {attr.val}
                              </Typography>
                            </Box>
                          )}
                      </ListItem>
                    ))}
                  <Box className={'selected-deal-attribute-container'}>
                    <Typography
                      className={'selected-deal-attribute-label-text'}
                      style={{ color: theme.textLight }}
                    >
                      Setter
                    </Typography>
                    <Box className={'selected-deal-owner-right'}>
                      {deal.setter?.profilePicture ? (
                        <Avatar
                          className={'selected-deal-owner-profile-picture'}
                          src={deal.setter?.profilePicture}
                        />
                      ) : (
                        <Avatar
                          className={'selected-deal-owner-profile-picture'}
                        >
                          <Typography style={{ fontSize: 12, fontWeight: 600 }}>
                            {deal.setter?.user?.initials}
                          </Typography>
                        </Avatar>
                      )}
                      <Typography
                        className={'selected-deal-owner-text'}
                        style={{ color: theme.text }}
                      >
                        {deal.setter?.user?.name}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className={'selected-deal-attribute-container'}>
                    <Typography
                      className={'selected-deal-attribute-label-text'}
                      style={{ color: theme.textLight }}
                    >
                      Closer
                    </Typography>
                    <Box className={'selected-deal-owner-right'}>
                      {deal.closer ? (
                        deal?.closer?.profilePicture ? (
                          <Avatar
                            className={'selected-deal-owner-profile-picture'}
                            src={deal?.closer?.profilePicture}
                          />
                        ) : (
                          <Avatar
                            className={'selected-deal-owner-profile-picture'}
                          >
                            <Typography
                              style={{ fontSize: 12, fontWeight: 600 }}
                            >
                              {deal?.closer?.user?.initials}
                            </Typography>
                          </Avatar>
                        )
                      ) : (
                        <Box
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: theme.stroke,
                            width: 24,
                            height: 24,
                            borderRadius: 1000,
                            marginRight: 8,
                          }}
                        >
                          <GamifyIcon
                            icon={'user'}
                            width={'14'}
                            height={'16'}
                            color={theme.textLight}
                          ></GamifyIcon>
                        </Box>
                      )}
                      <Typography
                        className={'selected-deal-owner-text'}
                        style={{ color: theme.text }}
                      >
                        {deal.closer ? deal?.closer?.user?.name : 'Unassigned'}
                      </Typography>
                    </Box>
                  </Box>
                </List>
                {powerMainDisplayAttrs && (
                  <>
                    <Typography
                      className={'selected-deal-section-header-text'}
                      style={{ color: theme.textLight }}
                    >
                      {powerMainDisplayAttrs.label.toUpperCase()}
                    </Typography>
                    <List>
                      <ListItem
                        key={1}
                        sx={{
                          padding: 0,
                        }}
                      >
                        <Box className={'selected-deal-attribute-container'}>
                          <Typography
                            className={'selected-deal-attribute-label-text'}
                            style={{ color: theme.textLight }}
                          >
                            Utility Company
                          </Typography>
                          <Typography
                            className={'selected-deal-attribute-val-text'}
                            style={{ color: theme.text }}
                          >
                            {powerMainDisplayAttrs.val.name}
                          </Typography>
                        </Box>
                      </ListItem>
                      <ListItem
                        key={0}
                        sx={{
                          padding: 0,
                        }}
                      >
                        <Box className={'selected-deal-attribute-container'}>
                          <Typography
                            className={'selected-deal-attribute-label-text'}
                            style={{ color: theme.textLight }}
                          >
                            {powerMainDisplayAttrs.val.utilityInfo}
                          </Typography>
                          {powerMainDisplayAttrs.val.utilityInfo ===
                          'Monthly Usage' ? (
                            <Box>
                              <Typography
                                className={'selected-deal-attribute-val-text'}
                                style={{ color: theme.text }}
                              >
                                {`Jan: ${powerMainDisplayAttrs.val.info.jan} KWs, Feb: ${powerMainDisplayAttrs.val.info.feb} KWs, Mar: ${powerMainDisplayAttrs.val.info.mar} KWs`}
                              </Typography>
                              <Typography
                                className={'selected-deal-attribute-val-text'}
                                style={{ color: theme.text }}
                              >
                                {`Apr: ${powerMainDisplayAttrs.val.info.apr} KWs, May: ${powerMainDisplayAttrs.val.info.may} KWs, Jun: ${powerMainDisplayAttrs.val.info.jun} KWs`}
                              </Typography>

                              <Typography
                                className={'selected-deal-attribute-val-text'}
                                style={{ color: theme.text }}
                              >
                                {`Jul: ${powerMainDisplayAttrs.val.info.jul} KWs, Aug: ${powerMainDisplayAttrs.val.info.aug} KWs, Sep: ${powerMainDisplayAttrs.val.info.sep} KWs`}
                              </Typography>
                              <Typography
                                className={'selected-deal-attribute-val-text'}
                                style={{ color: theme.text }}
                              >
                                {`Oct: ${powerMainDisplayAttrs.val.info.oct} KWs, Nov: ${powerMainDisplayAttrs.val.info.nov} KWs, Dec: ${powerMainDisplayAttrs.val.info.dec} KWs`}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography
                              className={'selected-deal-attribute-val-text'}
                              style={{ color: theme.text }}
                            >
                              {powerMainDisplayAttrs.val.utilityInfo ===
                              'Annual Usage'
                                ? `${powerMainDisplayAttrs.val.info} KWs`
                                : powerMainDisplayAttrs.val.utilityInfo ===
                                  'Average Monthly Cost'
                                ? `${powerMainDisplayAttrs.val.info} KWs/month`
                                : `[MONTHLY USAGE DISPLAY]`}
                            </Typography>
                          )}
                        </Box>
                      </ListItem>
                    </List>
                  </>
                )}
                {kwsMainDisplayAttrs.length > 0 && (
                  <>
                    <Typography
                      className={'selected-deal-section-header-text'}
                      style={{ color: theme.textLight }}
                    >
                      DEAL
                    </Typography>
                    {kwsMainDisplayAttrs.map((attr, i) => (
                      <ListItem
                        key={i}
                        sx={{
                          padding: 0,
                        }}
                      >
                        <Box className={'selected-deal-attribute-container'}>
                          <Typography
                            className={'selected-deal-attribute-label-text'}
                            style={{ color: theme.textLight }}
                          >
                            {attr.label}
                          </Typography>
                          <Typography
                            className={'selected-deal-attribute-val-text'}
                            style={{ color: theme.text }}
                          >
                            {attr.val}
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </>
                )}
                {/* NOTE: Only users that can edit a deal can add a note */}
                {showEdit && (
                  <>
                    {' '}
                    <Typography
                      className={'selected-deal-section-header-text'}
                      style={{ color: theme.textLight }}
                    >
                      ADD NOTES
                    </Typography>
                    <TextareaAutosize
                      className={'free-notes-text-area'}
                      style={{
                        backgroundColor: theme.background2,
                        color: theme.text,
                        border: 'none',
                        borderRadius: 8,
                        width: '100%',
                        resize: 'none',
                        padding: 12,
                      }}
                      placeholder={'Click to add'}
                      value={formikProps.values.notes}
                      onChange={(e) => {
                        formikProps.setFieldValue('notes', e.target.value);
                      }}
                      onFocus={() => {
                        setAddNoteFocused(true);
                        if (editNoteId) {
                          formikProps.setFieldValue(
                            `notesMap[${editNoteId}]`,
                            formikProps.initialValues.notesMap[editNoteId]
                          );
                          setEditNoteId(null);
                        }
                      }}
                      onBlur={() => {
                        setAddNoteFocused(false);
                      }}
                    ></TextareaAutosize>
                    {(addNoteFocused ||
                      formikProps.values.notes.length > 0) && (
                      <Box
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          width: '100%',
                          marginTop: '16px',
                        }}
                      >
                        <Box
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: 163,
                            height: 52,
                            backgroundColor:
                              formikProps.values.notes.length > 0
                                ? theme.primary
                                : theme.textLight,
                            padding: '24px 16px 24px 16px',
                            borderRadius: 8,
                          }}
                          sx={{
                            cursor:
                              formikProps.values.notes.length === 0
                                ? 'default'
                                : 'pointer',
                          }}
                          onClick={() => {
                            // Do nothing if no note inputted
                            if (formikProps.values.notes.length === 0) return;
                            formikProps.handleSubmit();
                          }}
                        >
                          {formikProps.isSubmitting ? (
                            <CircularProgress
                              style={{ color: 'white', width: 24, height: 24 }}
                            ></CircularProgress>
                          ) : (
                            <Typography
                              style={{
                                color: 'white',
                                fontSize: '18px',
                                fontWeight: 700,
                              }}
                            >
                              Leave Note
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Box>
              <Box className={'selected-deal-section-title-container'}>
                <Typography
                  className={'selected-deal-section-title-text'}
                  style={{ color: theme.text }}
                >
                  Appointments
                </Typography>
              </Box>
              {appointments.length > 0 ? (
                appointments.map((appointment, i) => (
                  <Box
                    key={i}
                    className={'selected-deal-appointment-section'}
                    style={{ backgroundColor: theme.background3 }}
                  >
                    <Box className={'selected-deal-appointment-top-section'}>
                      <Box
                        className={
                          'selected-deal-appointment-top-left-container'
                        }
                      >
                        <CalendarIcon color={theme.text} />
                        <Typography
                          className={'selected-deal-appointment-date-text'}
                          style={{ color: theme.text }}
                        >
                          {formatScheduleDate(
                            appointment.appointmentDate,
                            appointment.appointmentStartTime
                          )}
                        </Typography>
                      </Box>
                      <Box
                        className={
                          'selected-deal-appointment-top-right-container'
                        }
                      ></Box>
                    </Box>
                    <Box className={'selected-deal-appointment-bottom-section'}>
                      <Typography
                        className={'selected-deal-appointment-details-text'}
                        style={{ color: theme.textLight }}
                      >
                        {appointment.deal.name}
                      </Typography>
                      <Typography
                        className={'selected-deal-appointment-details-text'}
                        style={{ color: theme.textLight }}
                      >
                        Assigned to {appointment.user?.name}
                      </Typography>
                      <Typography
                        className={'selected-deal-appointment-details-text'}
                        style={{ color: theme.textLight }}
                      >
                        Setter: {appointment.setter?.name}
                      </Typography>
                      <Typography
                        className={'selected-deal-appointment-details-text'}
                        style={{ color: theme.textLight }}
                      >
                        Status: {appointment.status}
                      </Typography>
                    </Box>
                  </Box>
                ))
              ) : (
                <Box
                  className={'selected-deal-no-appointments-section'}
                  style={{ backgroundColor: theme.background3 }}
                >
                  <Typography
                    className={'no-appointments-text'}
                    style={{ color: theme.textLight }}
                  >
                    No appointments yet.
                  </Typography>
                </Box>
              )}

              {/* File Tabs Section - Photos, Reports, Estimates, etc. */}
              <Box
                className="selected-deal-section-title-container"
                sx={{ mt: 2, flexDirection: 'column' }}
              >
                <DealTabbedContent
                  deal={deal}
                  dealId={dealId}
                  comments={comments}
                  categoryId={categoryId}
                  appointments={appointments}
                  isOnKanban={isOnKanban}
                  showEdit={showEdit}
                  isReadOnly={isReadOnly}
                  isImageUploading={isImageUploading}
                  setIsImageUploading={setIsImageUploading}
                  onTriggerSelectedDealRefetch={onTriggerSelectedDealRefetch}
                  handleRefetchDeals={handleRefetchDeals}
                  handleImageClick={handleImageClick}
                  onImagesChange={(photos) => {
                    const imageNames = photos.map((photo) => photo.imageName);
                    setCarouselImages(imageNames);
                    setCarouselPhotos(photos);
                  }}
                />
              </Box>
            </>
          )}
        </Formik>
        {!hideDealMetaData && (
          <Box
            className={'selected-deal-meta-data-container'}
            style={{ backgroundColor: theme.background3 }}
          >
            <Button
              className={'selected-deal-meta-data-button'}
              onClick={handleMetaDataClick}
              disableRipple={true}
            >
              <Typography
                className={'selected-deal-meta-data-button-text'}
                style={{ color: '#FFFFFF' }}
              >
                {selectedDealMetaDataOpen
                  ? 'Hide Deal Meta Data'
                  : 'Show Deal Meta Data'}
              </Typography>
            </Button>
            {selectedDealMetaDataOpen && (
              <Box className={'selected-deal-meta-data-content-container'}>
                <Box className={'selected-deal-meta-data-attribute-container'}>
                  <Typography
                    className={'selected-deal-meta-data-attribute-text'}
                    style={{ color: theme.textLight }}
                  >
                    Deal ID:
                  </Typography>
                  <Typography
                    className={'selected-deal-meta-data-attribute-text'}
                    style={{ color: theme.text }}
                  >
                    {metaData.id}
                  </Typography>
                </Box>
                <Box className={'selected-deal-meta-data-attribute-container'}>
                  <Typography
                    className={'selected-deal-meta-data-attribute-text'}
                    style={{ color: theme.textLight }}
                  >
                    Deal Ext ID:
                  </Typography>
                  <Typography
                    className={'selected-deal-meta-data-attribute-text'}
                    style={{ color: theme.text }}
                  >
                    {metaData.extID}
                  </Typography>
                </Box>
                <Box className={'selected-deal-meta-data-attribute-container'}>
                  <Typography
                    className={'selected-deal-meta-data-attribute-text'}
                    style={{ color: theme.textLight }}
                  >
                    Created At:
                  </Typography>
                  <Typography
                    className={'selected-deal-meta-data-attribute-text'}
                    style={{ color: theme.text }}
                  >
                    {metaData.createdAt}
                  </Typography>
                </Box>
                {metaData?.createdBy && (
                  <>
                    <Typography
                      className={'selected-deal-meta-data-label'}
                      style={{ color: theme.textLight }}
                    >
                      Created By:
                    </Typography>
                    <Box
                      className={'selected-deal-meta-data-attribute-container'}
                    >
                      <Typography
                        className={'selected-deal-meta-data-sub-label'}
                        style={{ color: theme.textLight }}
                      >
                        Name:
                      </Typography>
                      <Typography
                        className={'selected-deal-meta-data-attribute-text'}
                        style={{ color: theme.text }}
                      >
                        {metaData?.createdBy?.name}
                      </Typography>
                    </Box>
                    <Box
                      className={'selected-deal-meta-data-attribute-container'}
                    >
                      <Typography
                        className={'selected-deal-meta-data-sub-label'}
                        style={{ color: theme.textLight }}
                      >
                        Email:
                      </Typography>
                      <Typography
                        className={'selected-deal-meta-data-attribute-text'}
                        style={{ color: theme.text }}
                      >
                        {metaData?.createdBy?.email}
                      </Typography>
                    </Box>
                    <Box
                      className={'selected-deal-meta-data-attribute-container'}
                    >
                      <Typography
                        className={'selected-deal-meta-data-sub-label'}
                        style={{ color: theme.textLight }}
                      >
                        User ID:
                      </Typography>
                      <Typography
                        className={'selected-deal-meta-data-attribute-text'}
                        style={{ color: theme.text }}
                      >
                        {metaData?.createdBy?.id}
                      </Typography>
                    </Box>
                    <Box
                      className={'selected-deal-meta-data-attribute-container'}
                    >
                      <Typography
                        className={'selected-deal-meta-data-sub-label'}
                        style={{ color: theme.textLight }}
                      >
                        User Ext ID:
                      </Typography>
                      <Typography
                        className={'selected-deal-meta-data-attribute-text'}
                        style={{ color: theme.text }}
                      >
                        {metaData?.createdBy?.extID}
                      </Typography>
                    </Box>
                  </>
                )}
                <Box className={'selected-deal-meta-data-attribute-container'}>
                  <Typography
                    className={'selected-deal-meta-data-label'}
                    style={{ color: theme.textLight }}
                  >
                    Updated At:
                  </Typography>
                  <Typography
                    className={'selected-deal-meta-data-attribute-text'}
                    style={{ color: theme.text }}
                  >
                    {metaData?.updatedAt}
                  </Typography>
                </Box>
                {metaData.updatedBy && (
                  <>
                    <Typography
                      className={'selected-deal-meta-data-label'}
                      style={{ color: theme.textLight }}
                    >
                      Updated By:
                    </Typography>
                    <Box
                      className={'selected-deal-meta-data-attribute-container'}
                    >
                      <Typography
                        className={'selected-deal-meta-data-sub-label'}
                        style={{ color: theme.textLight }}
                      >
                        Name:
                      </Typography>
                      <Typography
                        className={'selected-deal-meta-data-attribute-text'}
                        style={{ color: theme.text }}
                      >
                        {metaData?.updatedBy?.name}
                      </Typography>
                    </Box>
                    <Box
                      className={'selected-deal-meta-data-attribute-container'}
                    >
                      <Typography
                        className={'selected-deal-meta-data-sub-label'}
                        style={{ color: theme.textLight }}
                      >
                        Email:
                      </Typography>
                      <Typography
                        className={'selected-deal-meta-data-attribute-text'}
                        style={{ color: theme.text }}
                      >
                        {metaData?.updatedBy?.email}
                      </Typography>
                    </Box>
                    <Box
                      className={'selected-deal-meta-data-attribute-container'}
                    >
                      <Typography
                        className={'selected-deal-meta-data-sub-label'}
                        style={{ color: theme.textLight }}
                      >
                        User ID:
                      </Typography>
                      <Typography
                        className={'selected-deal-meta-data-attribute-text'}
                        style={{ color: theme.text }}
                      >
                        {metaData?.updatedBy?.id}
                      </Typography>
                    </Box>
                    <Box
                      className={'selected-deal-meta-data-attribute-container'}
                    >
                      <Typography
                        className={'selected-deal-meta-data-sub-label'}
                        style={{ color: theme.textLight }}
                      >
                        User Ext ID:
                      </Typography>
                      <Typography
                        className={'selected-deal-meta-data-attribute-text'}
                        style={{ color: theme.text }}
                      >
                        {metaData?.updatedBy?.extID}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        )}
        <Box className="selected-deal-bottom-section"></Box>
      </Box>
    </>
  );
}
